<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import EventCard from 'dashboard/routes/dashboard/planning/events/EventCard.vue';
import EventLostCancellationModal from 'dashboard/routes/dashboard/planning/events/EventLostCancellationModal.vue';
import { buildOrderedStatusOptions } from 'dashboard/routes/dashboard/planning/events/statusOptions';
import { useLostStageUpdate } from 'dashboard/routes/dashboard/planning/events/useLostStageUpdate';
import { useConfetti } from 'dashboard/composables/useConfetti';
import ContactsAPI from 'dashboard/api/contacts';
import PlanningInquiryButton from 'dashboard/components/widgets/conversation/PlanningInquiryButton.vue';

const props = defineProps({
  contactId: {
    type: Number,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const store = useStore();
const { t } = useI18n();
const { triggerConfetti } = useConfetti();
const {
  showLostCancellationModal,
  pendingLostUpdate,
  lostCancellationSaving,
  updateEventStage,
  confirmLostCancellation,
  cancelLostCancellation,
} = useLostStageUpdate();

const events = ref([]);
const isLoading = ref(false);
const statusOptions = ref([]);

const eventLinkQuery = computed(() =>
  props.conversationId ? { fromConversation: props.conversationId } : null
);

const patchEvent = (eventId, updates) => {
  events.value = events.value.map(event =>
    event.id === eventId ? { ...event, ...updates } : event
  );
};

const fetchEvents = async () => {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await ContactsAPI.getContactEvents(
      props.contactId,
      props.conversationId
    );
    events.value = data.data || [];
  } catch {
    events.value = [];
  } finally {
    isLoading.value = false;
  }
};

const loadStatusOptions = async () => {
  const response = await store.dispatch('planningEvents/getStatusOptions');
  statusOptions.value = [...buildOrderedStatusOptions(response)];
};

const handleStatusUpdate = async ({ eventId, system_stage, custom_status }) => {
  const eventRecord = events.value.find(item => item.id === eventId);
  if (!eventRecord) return;

  try {
    const updatedEvent = await updateEventStage(eventRecord, {
      system_stage,
      custom_status,
    });
    if (!updatedEvent) return;
    patchEvent(eventId, updatedEvent);
    if (system_stage === 'confirmed') triggerConfetti();
  } catch (error) {
    console.error('Failed to update event status:', error);
  }
};

onMounted(() => {
  loadStatusOptions();
  fetchEvents();
});

watch(
  () => [props.contactId, props.conversationId],
  () => {
    fetchEvents();
  }
);
</script>

<template>
  <div class="px-4 py-2">
    <div v-if="conversationId" class="flex justify-end mb-2">
      <PlanningInquiryButton
        :conversation-id="conversationId"
        @created="fetchEvents"
      />
    </div>
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <div
        class="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"
      />
    </div>

    <div v-else-if="events.length === 0" class="py-8 text-center">
      <Icon
        icon="i-lucide-calendar-off"
        class="size-8 mx-auto mb-2 text-slate-400"
      />
      <p class="text-sm text-slate-500 dark:text-slate-400">
        {{ t('CONTACT_PANEL.EVENTS.NO_EVENTS') }}
      </p>
    </div>

    <div v-else class="space-y-2">
      <EventCard
        v-for="event in events"
        :key="event.id"
        :event="event"
        compact
        :status-options="statusOptions"
        :link-query="eventLinkQuery"
        @update-status="handleStatusUpdate"
      />
    </div>

    <EventLostCancellationModal
      v-model="showLostCancellationModal"
      :event="pendingLostUpdate?.event"
      :is-loading="lostCancellationSaving"
      @confirm="confirmLostCancellation"
      @cancel="cancelLostCancellation"
    />
  </div>
</template>
